<template>
  <div class="carousel">
    <div
      ref="visibleContainer"
      class="wrapper"
      @touchstart="handleTouchStart"
      @touchend="handleTouchEnd"
      @touchmove.prevent
    >
      <div ref="wrapper" class="scroll-wrapper">
        <slot />
      </div>
    </div>
    <div class="controls">
      <cv-button
        class="icon-button previous"
        kind="ghost"
        size="small"
        :disabled="disablePreviousButton"
        @click="handlePrevious"
      >
        <svg-icon name="ArrowLeft24" kind="action" />
      </cv-button>
      <cv-button
        class="icon-button next"
        kind="ghost"
        size="small"
        :disabled="disableNextButton"
        @click="handleNext"
      >
        <svg-icon name="ArrowRight24" kind="action" />
      </cv-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import DeviceUtils from "@utils/device.utils";
import { debounceFn } from "@/utils/debounce";
import { ref, computed, watch, onMounted, onUpdated, useTemplateRef } from "vue";
import { useVm } from "@utils/vm.ts";

interface Props {
  startIndex?: number;
  slideMove?: number | string;
}

const { startIndex = 0, slideMove = "auto" } = defineProps<Props>();

const { vm } = useVm();

// Template refs
const visibleContainer = useTemplateRef<HTMLElement>("visibleContainer");
const wrapper = useTemplateRef<HTMLElement>("wrapper");

// Data
const firstShownIndex = ref(startIndex);
const itemAmount = ref(0);
const itemShown = ref(0);
const itemWidth = ref(0);
const touchStartX = ref(0);
const touchEndX = ref(0);
const carouselObserver = ref<ResizeObserver | null>(null);

// Computed
const disablePreviousButton = computed(() => {
  return firstShownIndex.value === 0;
});

const disableNextButton = computed(() => {
  return firstShownIndex.value >= itemAmount.value - itemShown.value;
});

const slideCount = computed(() => {
  return DeviceUtils.isMobile() ? 1 : 3;
});

// Methods
const handleTouchStart = (e) => {
  touchStartX.value = e.changedTouches[0].screenX;
};

const handleTouchEnd = (e) => {
  touchEndX.value = e.changedTouches[0].screenX;

  if (touchStartX.value !== touchEndX.value) {
    handleScroll();
  }
};

const handleScroll = () => {
  if (touchStartX.value > touchEndX.value) {
    if (disableNextButton.value) {
      return;
    }
    handleMove("right");
  } else {
    if (disablePreviousButton.value) {
      return;
    }
    handleMove("left");
  }
};

const handleMove = (direction) => {
  const scrollWrapper = wrapper.value;
  if (!scrollWrapper) return;

  if (direction === "left") {
    if (slideMove === "auto") {
      firstShownIndex.value -= itemShown.value;
    } else {
      firstShownIndex.value -= slideMove;
    }
  } else {
    if (slideMove === "auto") {
      firstShownIndex.value += itemShown.value;
    } else {
      // [SLS-8586] - cater to always show 3 cards even if item amount is not multiple of 3
      if (
        !DeviceUtils.isMobile() &&
        itemAmount.value > slideCount.value &&
        itemAmount.value < firstShownIndex.value + slideCount.value + slideCount.value
      ) {
        firstShownIndex.value = itemAmount.value - slideCount.value;
      } else {
        firstShownIndex.value += slideCount.value;
      }
    }
  }

  if (firstShownIndex.value < 0) {
    firstShownIndex.value = 0;
  }

  if (firstShownIndex.value > itemAmount.value - itemShown.value) {
    firstShownIndex.value = itemAmount.value - itemShown.value;
  }

  // gap will be total of margin-left & margin-right of card
  const carouselItem = DOMUtils.elOf(vm.$el, ".carousel-item")[0];

  const gapWidth = parseInt(window.getComputedStyle(carouselItem).marginRight) * 2;

  const blockWidth = itemWidth.value + gapWidth;

  const coordinate = firstShownIndex.value * blockWidth;

  scrollWrapper.style.transform = `translateX(${-coordinate}px)`;
};

const handlePrevious = () => {
  handleMove("left");
};

const handleNext = () => {
  handleMove("right");
};

const calculateVar = () => {
  setTimeout(() => {
    if (!wrapper.value) return;

    const carouselItem = wrapper.value.querySelector(".carousel-item");
    if (carouselItem) {
      itemWidth.value = carouselItem.getBoundingClientRect().width;
      itemShown.value = Math.floor(wrapper.value.clientWidth / (itemWidth.value + 8));

      handleUpdated();
    }
  }, 10); // 10ms delay to allow the carousel item to render actual sizing
};

const handleUpdated = () => {
  if (!wrapper.value) return;

  itemAmount.value = wrapper.value.children.length;

  // [SLS-8586] - cater to always show full row of cards even if item amount is not multiple of item shown
  if (firstShownIndex.value > 0 && firstShownIndex.value + itemShown.value > itemAmount.value) {
    firstShownIndex.value = itemAmount.value - itemShown.value;
  }
  // gap will be total of margin-left & margin-right of card
  const carouselItem = DOMUtils.elOf(vm.$el, ".carousel-item")[0];
  if (!carouselItem) return;

  const gapWidth = parseInt(window.getComputedStyle(carouselItem).marginRight) * 2;

  const blockWidth = itemWidth.value + gapWidth;

  const coordinate = firstShownIndex.value * blockWidth;

  wrapper.value.style.transform = `translateX(${-coordinate}px)`;
};

const initCarouselObserver = () => {
  carouselObserver.value = new ResizeObserver(
    debounceFn(
      () => {
        // when container size changes, transform can be recalculated
        calculateVar();
      },
      1000,
      {
        leading: true,
      },
    ),
  );

  if (visibleContainer.value) {
    carouselObserver.value.observe(visibleContainer.value);
  }
};

// Watch
watch(
  () => startIndex,
  (value) => {
    // shift index value so it will move to correct item after handleMove is called
    const slide = slideMove === "auto" ? itemShown.value : slideMove;
    if (firstShownIndex.value < value) {
      firstShownIndex.value = value - slide;
      handleMove("right");
    } else {
      firstShownIndex.value = value + slide;
      handleMove("left");
    }
  },
);

// Lifecycle hooks
onMounted(() => {
  initCarouselObserver();
});

onUpdated(() => {
  handleUpdated();
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.carousel {
  position: relative;

  > .controls {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    right: 0;
    height: 2.5rem;
    pointer-events: none;
    z-index: 1;

    .bx--btn {
      padding: 0;
      width: 2.5rem;
      height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid;
      border-radius: 50%;
      position: absolute;
      pointer-events: all;

      &:disabled {
        opacity: 1; // to fix browser default on half opacity for disabled button
        border-color: $disabled-02;
      }

      @include media("mobile") {
        display: none;
      }

      .sls-icon {
        flex: 0 0 auto;
      }
    }

    .previous {
      left: 0;
    }

    .next {
      right: 0;
    }
  }

  > .wrapper {
    z-index: 2;

    @include media("mobile") {
      overflow: auto;
    }
    @include media("tablet-min") {
      max-width: calc(100% - #{$spacing-08} - #{$spacing-08});
      margin-left: $spacing-08;
      margin-right: $spacing-08;
      overflow: hidden;
    }
  }

  .scroll-wrapper {
    display: flex;
    flex-wrap: nowrap;
    transition: ease 500ms;
  }
}
</style>
